import {AfterContentInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder}                                               from '@angular/forms';
import {IGridData}                                                 from '../../../ap-interface';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                                  from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynComponentComponent
}                                                                  from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridPagerConfig
}                                                                  from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  ApDynGridStatusColumnConfig
}                                                                  from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                                                  from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {filter, map}                                               from 'rxjs/operators';
import {FormStore}                                                 from '../../../stores/layout/form.store';
import {TerminalsStore}                                            from '../../../stores/base-data/terminals.store';
import {Observable, Subscription}                                  from 'rxjs';
import {CampaignYearStore}                                         from '../../../stores/login/campaignyear.store';
import {
  ApDynGridGroupColumnConfig
}                                                                  from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {LanguageStore}                                             from '../../../stores/translation/language.store';
import {ApDateService}                                             from '../../../ap-core/services/ap-date-service';
import ITerminals = Data.BaseData.ITerminals;

interface ITerminalsGridData extends IGridData<ITerminals> {
  Status: number;
  StatusClass: string;
  StatusDesc: string;
  LastServiceDate: Date;
}

/**
 * Component for the terminals overview
 */
@Component({
  selector: 'ap-terminals-overview',
  template: `
    <ap-dyncomponent [columns]="columns"
                     [items]="items$ | async"
                     [pager]="pager"
                     [loading$]="terminalsLoading$">
    </ap-dyncomponent>`
})

/**
 * Terminals overview component.
 */
export class ApTerminalsOverviewComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent;
  public terminalsLoading$: Observable<boolean>;
  public items$: Observable<ITerminalsGridData[]>;
  public columns: ApDynGridColumnConfigBase[];
  public pager: ApDynGridPagerConfig;
  private _subscriptions: Subscription[] = [];

  constructor(public terminalsStore: TerminalsStore,
              private fb: FormBuilder,
              private formStore: FormStore,
              private languageStore: LanguageStore,
              private campaignYearStore: CampaignYearStore,
              private dateService: ApDateService) {
    this.terminalsLoading$ = this.terminalsStore.Listen(s => s.loading);
    this.items$ = this.terminalsStore.Listen(s => s.data).pipe(
      filter(d => !!d),
      map(d => d.Convert<ITerminalsGridData>(terminal => ({
        Id: terminal.Id,
        SourceItem: terminal,
        // Dummy status. We do not have deleted or non-active terminals in our database. They are deleted by ERP-Sync Job
        Status: 1,
        StatusClass: 'ap-status-green',
        StatusDesc: 'Global__Active',
        LastServiceDate: this.dateService.getDateMidnight(terminal.LastService),
      }))),
    );
  }

  ngAfterContentInit(): void {
    this.gridBuilder();
  }

  ngOnInit(): void {
    this._subscriptions.push(this.campaignYearStore.Listen(s => s.selectedYear).subscribe(() => {
      this.terminalsStore.loadTerminals();
    }));
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Build grid columns
   */
  gridBuilder(): void {
    this.columns = [
      new ApDynGridStatusColumnConfig({
        field: 'Status', showHeaderTitle: false,
        class: 'StatusClass', description: 'StatusDesc', filterable: true
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Terminal',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: 'SourceItem.Terminal', title: 'Tasks_ViewModel__Type',
            filterable: true,
            headerFilterable: true, sortIndex: 0
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'SourceItem.TerminalSn', title: 'Global__SerialNumber',
            filterable: true,
            headerFilterable: true,
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            })
          })]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Sensor',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: 'SourceItem.Sensor', title: 'Tasks_ViewModel__Type',
            filterable: true,
            headerFilterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'SourceItem.SensorSn', title: 'Global__SerialNumber',
            filterable: true,
            headerFilterable: true,
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            })
          })]
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.MailAddress', title: 'Global__Email',
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'LastServiceDate', title: 'Global_Last_Update',
        filterable: true,
        headerFilterable: true,
        format: this.languageStore.SelectedLanguage.DateFormat,
        filterType: FilterType.DATE
      })
    ];
    this.pager = new ApDynGridPagerConfig();
  }
}
